import { isClickedOutside } from '@theme/utilities';

class DropdownGroupComponent extends HTMLElement {
  constructor() {
    super();

    this.dropdowns = [];
  }

  connectedCallback() {
    this.dropdowns = this.querySelectorAll('dropdown-component');

    document.addEventListener('click', this.#handleDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  #handleDocumentClick = (event) => {
    const clickedInsideDropdown = event.target.closest('dropdown-component');
    if (!clickedInsideDropdown) {
      this.closeAllDropdowns();
    }
  };

  closeAllDropdowns() {
    this.dropdowns.forEach((dropdown) => {
      dropdown.close();
    });
  }
}

if (!customElements.get('dropdown-group-component')) {
  customElements.define('dropdown-group-component', DropdownGroupComponent);
}

class DropdownComponent extends HTMLElement {
  constructor() {
    super();

    this.toggle = null;
    this.content = null;
    this.isOpen = false;
  }

  connectedCallback() {
    this.toggle = this.querySelector('[data-ref="dropdown-toggle"]');
    this.content = this.querySelector('[data-ref="dropdown-content"]');

    this.toggle?.addEventListener('click', this.#handleToggle);

    /*
     * 🔴 LE MÉGA-MENU S'OUVRE AU SURVOL, PAS AU CLIC. Demandé par l'humain le
     *    2026-10-12 : « je veux quand je passe la souris dessus le menu il s'ouvre,
     *    là faut que je clic ». Le thème n'ouvrait qu'au clic, pour TOUS ses
     *    déroulants.
     *
     * ⚠️ ON NE TOUCHE QUE LE MÉGA-MENU. Les mêmes `dropdown-component` servent au
     *    tri et aux filtres d'une page collection : les ouvrir au survol y
     *    déclencherait un panneau dès que la souris passe, en plein défilement.
     *    D'où le filtre sur la classe.
     *
     * ⚠️ SUR ÉCRAN TACTILE, RIEN NE CHANGE. `(hover: hover)` écarte les appareils
     *    où le « survol » est en réalité un premier appui : sans ce test, il
     *    faudrait deux taps pour suivre un lien, et le premier ouvrirait un
     *    panneau qu'on ne voulait pas.
     */
    this.estMegamenu = this.classList.contains('header__menu-item--megamenu');
    this.peutSurvoler = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (this.estMegamenu && this.peutSurvoler) {
      this.addEventListener('pointerenter', this.#handlePointerEnter);
      this.addEventListener('pointerleave', this.#handlePointerLeave);
      /* Le clavier doit ouvrir comme la souris : sans ça, la navigation à la
         tabulation ne verrait jamais les rayons. */
      this.addEventListener('focusin', this.#handlePointerEnter);
      this.addEventListener('focusout', this.#handleFocusOut);
    }
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.#handleToggle);
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.removeEventListener('focusin', this.#handlePointerEnter);
    this.removeEventListener('focusout', this.#handleFocusOut);
    clearTimeout(this.minuterie);
  }

  #handlePointerEnter = () => {
    clearTimeout(this.minuterie);
    if (this.isOpen) return;
    this.closest('dropdown-group-component')?.closeAllDropdowns();
    this.open();
  };

  /*
   * ⚠️ UN DÉLAI DE FERMETURE EST NÉCESSAIRE. Entre le libellé et le panneau il y
   *    a la bordure de l'en-tête : sans ce répit, le menu se referme au moment
   *    précis où la souris descend vers les liens. 160 ms suffisent et ne se
   *    remarquent pas.
   */
  #handlePointerLeave = () => {
    clearTimeout(this.minuterie);
    this.minuterie = setTimeout(() => this.close(), 160);
  };

  #handleFocusOut = (event) => {
    if (!this.contains(event.relatedTarget)) this.close();
  };

  #handleToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    /* ⚠️ Au survol, le panneau est DÉJÀ ouvert quand la souris atteint le libellé.
       Un clic le refermerait — le geste naturel « j'ouvre puis je clique pour
       entrer » ferait exactement l'inverse de ce qu'on attend. Sur le méga-menu
       en mode survol, le clic ne ferme donc jamais : il ne fait rien. */
    if (this.estMegamenu && this.peutSurvoler && this.isOpen) {
      return;
    }

    if (this.isOpen) {
      this.close();
    } else {
      this.closest('dropdown-group-component')?.closeAllDropdowns();
      this.open();
    }
  };

  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.close();
    }
  };

  open() {
    this.setAttribute('data-open', 'true');
    document.addEventListener('click', this.#handleClickOutside);
    this.isOpen = true;
  }

  close() {
    this.setAttribute('data-open', 'false');
    document.removeEventListener('click', this.#handleClickOutside);
    this.isOpen = false;
  }
}

if (!customElements.get('dropdown-component')) {
  customElements.define('dropdown-component', DropdownComponent);
}
